import { createDefaultMenuSetting } from "../actions/Settings";
import { isString, isNumber } from "lodash";
import Storage from "@aws-amplify/storage";

/**
 * Function: formatMenuGeneralSetting
 * Used to flatten the menu object then used it to config the style of menu
 *
 * NOTE: If format menus in `Prev`, alway set "isPublished": true so user can
 * alway see this menu display on page
 */
export const formatMenusGeneralSetting = (oldMenus, newMenu) => {
  let updateMenus = oldMenus;

  if (newMenu) {
    updateMenus = [
      ...oldMenus.filter((menu) => menu.id !== newMenu.id),
      newMenu,
    ];
  }

  const defaultMenuSetting = createDefaultMenuSetting();

  return updateMenus.map((menu) => {
    const menuId = menu.id;
    const jsonPath = `menu-${menuId}.json`;
    const name = menu.name;
    const menuLayout = menu?.menuLayout || "DEFAULT";

    // Update the menuSetting. If old menu don't have menuSetting, use default setting
    const setting = { ...defaultMenuSetting, ...menu.menuSetting };

    return {
      ...setting,
      id: menuId,
      name,
      jsonPath,
      menuLayout,
      isPublished: true,
    };
  });
};

/**
 * Function: isPlaceholderMenu
 * A menu fed by the BinWise integration keeps a placeholder upload file whose rows
 * are all "~". Its real menu-<id>.json is written by the integration, so it must
 * not be overwritten with the placeholder rows on publish.
 */
export const isPlaceholderMenu = (rows) =>
  Array.isArray(rows) &&
  rows.length > 0 &&
  rows.every((row) => `${row?.name ?? ""}`.trim() === "~");

/**
 * Function: shouldWriteMenuJson
 * Placeholder rows are written only when no menu json exists yet: parseFile
 * copies every published menu json, and a missing one fails the whole publish.
 */
export const shouldWriteMenuJson = async (rows, menuJsonKey) => {
  if (!isPlaceholderMenu(rows)) return true;
  const existing = await Storage.list(menuJsonKey);
  return !(existing || []).some((item) => item.key === menuJsonKey);
};

/**
 * Function: getMenuFileFromUrl
 * Used to get the file from url.
 * The file should be [".csv", ".xlxs"]
 */
export const getMenuFileFromUrl = async (url, name) => {
  try {
    let file = await fetch(url)
      .then((r) => r.blob())
      .then(
        (blobFile) =>
          new File([blobFile], name, {
            type:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
      );

    return file;
  } catch (e) {
    throw new Error("Get file error!");
  }
};

/**
 * Function: validateUrlImage
 * Used to validate is valid url-image
 */
export const validateUrlImage = ({ url, timeout = 60000 }) => {
  const ERROR_MESSAGE_LOAD_FAILED = "Image load failed";
  const ERROR_MESSAGE_LOAD_TIMED_OUT = "Image load timed out";

  if (!url || !isString(url)) {
    throw new Error(`Invalid url parameter.  url: ${url}`);
  }

  if (!isNumber(timeout) || timeout < 1) {
    throw new Error(
      `Invalid timeout parameter.  Expected timeout >= 1.  timeout: ${timeout}`
    );
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    let timedOut = false;
    console.log("validateUrlImage", url);

    const errorHandler = () => {
      if (timedOut) {
        return;
      }
      clearTimeout(timer);

      reject(new Error(ERROR_MESSAGE_LOAD_FAILED));
    };

    const timer = setTimeout(() => {
      timedOut = true;
      // Reset .src to invalid URL so it stops previous fetch and does not trigger a new load.
      image.src = "//!!!/image.jpg";

      reject(new Error(ERROR_MESSAGE_LOAD_TIMED_OUT));
    }, timeout);

    image.onerror = image.onabort = errorHandler;

    image.onload = () => {
      // If fetch has timed out, the URL would be modified in order to cancel the fetch.
      // Therefore, "onload" would never be called when "timedOut" is true.

      /* istanbul ignore next */
      if (timedOut) {
        return;
      }
      clearTimeout(timer);
      resolve({ image, url });
    };
    image.src = url;
  });
};
